import { Alert, Button, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { clientProfilesDataSource } from "./clientProfilesDataSource";

interface Props {
  clientCode: string;
  sourceTemplateClientCode?: string;
  onSaved: (clientCode: string, sourceTemplateClientCode: string | undefined) => void;
}

const noTemplateValue = "";

const setDemoSourceTemplate = withErrorHandling(clientProfilesDataSource.setDemoSourceTemplate);

const ClientProfileSourceTemplateSection = ({ clientCode, sourceTemplateClientCode, onSaved }: Props) => {
  const [draftTemplateCode, setDraftTemplateCode] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveConfirmation, setSaveConfirmation] = useState("");

  const savedTemplateCode = sourceTemplateClientCode ?? noTemplateValue;
  const selectedTemplateCode = draftTemplateCode ?? savedTemplateCode;

  const getCandidates = useCallback(
    () => clientProfilesDataSource.getDemoSourceTemplateCandidates(clientCode),
    [clientCode]
  );
  const [candidates, fetchCandidatesError] = useFetch(getCandidates);

  const save = async (templateCode: string) => {
    setIsSaving(true);
    setSaveError("");
    setSaveConfirmation("");

    const savedCode = templateCode === noTemplateValue ? undefined : templateCode;
    const [, error] = await setDemoSourceTemplate({
      targetClientCode: clientCode,
      sourceTemplateClientCode: savedCode,
    });

    if (error) {
      logError(error, "[ClientProfileSourceTemplateSection] setDemoSourceTemplate");
      setSaveError(error.message);
      setIsSaving(false);
      return;
    }

    setDraftTemplateCode(undefined);
    setSaveConfirmation(
      templateCode === noTemplateValue ? "Source template cleared." : `Source template set to ${templateCode}.`
    );
    setIsSaving(false);
    onSaved(clientCode, savedCode);
  };

  const handleSaveClick = () => save(selectedTemplateCode);
  const handleClearClick = () => save(noTemplateValue);

  if (fetchCandidatesError) {
    logError(fetchCandidatesError, "[ClientProfileSourceTemplateSection] getDemoSourceTemplateCandidates");
    return <DataLoadingFailed text={fetchCandidatesError.message} />;
  }

  if (candidates === undefined) {
    return <InlineLoader />;
  }

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Source template</Typography>
      <Typography variant="caption" color="textSecondary">
        The Template company this demo company is refreshed from.
      </Typography>
      {saveError && (
        <Alert severity="error" variant="outlined">
          {saveError}
        </Alert>
      )}
      {saveConfirmation && (
        <Alert severity="success" variant="outlined">
          {saveConfirmation}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Select
          value={selectedTemplateCode}
          disabled={isSaving}
          displayEmpty
          sx={{ minWidth: "20rem" }}
          onChange={(event) => setDraftTemplateCode(event.target.value)}
        >
          <MenuItem value={noTemplateValue}>No source template</MenuItem>
          {candidates.items.map((candidate) => (
            <MenuItem key={candidate.clientCode} value={candidate.clientCode}>
              {candidate.title} ({candidate.clientCode})
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          disabled={isSaving || selectedTemplateCode === savedTemplateCode}
          onClick={handleSaveClick}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          disabled={isSaving || savedTemplateCode === noTemplateValue}
          onClick={handleClearClick}
        >
          Clear
        </Button>
      </Stack>
    </Stack>
  );
};

export default ClientProfileSourceTemplateSection;
